// Session Management Service
// Handles session start, heartbeat, and end API calls

const BASE_URL = 'https://v2.infopublisher.in/usage/sessions';

class SessionService {
  constructor() {
    this.sessionId = null;
    this.heartbeatInterval = null;
    this.isSessionActive = false;
    this.HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
    // this.ipAddress = '';
  }

  // Generate unique session ID
  generateSessionId() {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 10);
    return `usage_${timestamp}_${randomStr}`;
  }

  // Get current ISO timestamp
  // Returns timestamp in ISO 8601 format with timezone
  getCurrentTimestamp() {
    const now = new Date();
    
    // Format: YYYY-MM-DDTHH:mm:ss.sss+05:30 (IST)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    
    // Get timezone offset
    const timezoneOffset = -now.getTimezoneOffset();
    const offsetHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, '0');
    const offsetMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, '0');
    const offsetSign = timezoneOffset >= 0 ? '+' : '-';
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
  }

  // Get user's IP address (cached after first fetch)
  async getIPAddress() {
    if (this.ipAddress) {
      return this.ipAddress;
    }
    
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      // this.ipAddress = data.ip;
      return this.ipAddress;
    } catch (error) {
      console.log('Could not fetch IP address:', error);
      return '';
    }
  }

  // Get access token from localStorage or decrypt from service_acceptor
  getAccessToken() {
    // First try direct access_token
    let token = localStorage.getItem('access_token');
    
    if (token) {
      return token;
    }
    
    // Fallback: Try to get from encrypted service_acceptor (if using CryptoJS encryption)
    try {
      const serviceAcceptor = localStorage.getItem('service_acceptor');
      const serviceProvider = localStorage.getItem('service_provider');
      
      if (serviceAcceptor && serviceProvider) {
        // Import CryptoJS dynamically if needed
        const CryptoJS = require('crypto-js');
        const bytes = CryptoJS.AES.decrypt(serviceAcceptor, serviceProvider);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (decrypted) {
          const parsed = JSON.parse(decrypted);
          return parsed?.access_token || '';
        }
      }
    } catch (e) {
      console.log('Could not decrypt token:', e);
    }
    
    return '';
  }
  
  // Get student ID from sessionStorage
  getStudentId() {
    try {
      const studentData = sessionStorage.getItem('studentData');
      if (studentData) {
        const parsed = JSON.parse(studentData);
        return parsed[0]?.student_id || null;
      }
    } catch (e) {
      console.log('Error getting student ID');
    }
    return null;
  }

  // Start a new session
  async startSession() {
    if (this.isSessionActive) {
      console.log('Session already active');
      return;
    }

    try {
      const token = this.getAccessToken();
      
      // Don't start session if no token available
      if (!token) {
        console.log('No access token available, skipping session start');
        return;
      }

      this.sessionId = this.generateSessionId();
      const ipAddress = await this.getIPAddress();
      const studentId = this.getStudentId();

      const payload = {
        sessionId: this.sessionId,
        startedAt: this.getCurrentTimestamp(),
        ipAddress: ipAddress || '',
        device: 'web',
        // studentId: studentId
      };

      console.log('Starting session with token:', token ? 'Token present' : 'No token');
      console.log('Session payload:', payload);

      const response = await fetch(`${BASE_URL}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        console.log('Session started successfully:', this.sessionId);
        this.isSessionActive = true;
        
        // Store session ID in sessionStorage for persistence
        sessionStorage.setItem('currentSessionId', this.sessionId);
        sessionStorage.setItem('sessionStartTime', this.getCurrentTimestamp());
        
        // Start heartbeat
        this.startHeartbeat();
      } else {
        const errorText = await response.text();
        console.error('Failed to start session:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error starting session:', error);
    }
  }

  // Send heartbeat
  async sendHeartbeat() {
    if (!this.isSessionActive || !this.sessionId) {
      console.log('No active session for heartbeat');
      return;
    }

    try {
      const token = this.getAccessToken();

      const response = await fetch(`${BASE_URL}/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          timestamp: this.getCurrentTimestamp()
        })
      });

      if (response.ok) {
        console.log('Heartbeat sent:', this.getCurrentTimestamp());
      } else {
        console.error('Heartbeat failed:', response.status);
      }
    } catch (error) {
      console.error('Error sending heartbeat:', error);
    }
  }

  // Start heartbeat interval
  startHeartbeat() {
    // Clear any existing interval
    this.stopHeartbeat();
    
    // Send heartbeat every 10 minutes
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.HEARTBEAT_INTERVAL);
    
    console.log('Heartbeat started - will send every 5 minutes');
  }

  // Stop heartbeat interval
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('Heartbeat stopped');
    }
  }

  // End session
  async endSession() {
    if (!this.isSessionActive && !this.sessionId) {
      // Try to get session ID from sessionStorage
      this.sessionId = sessionStorage.getItem('currentSessionId');
      if (!this.sessionId) {
        console.log('No session to end');
        return;
      }
    }

    try {
      const token = this.getAccessToken();
      
      if (!token) {
        console.log('No access token available for ending session');
        return;
      }

      const payload = {
        sessionId: this.sessionId,
        endedAt: this.getCurrentTimestamp()
      };

      console.log('Ending session with token:', token ? 'Token present' : 'No token');
      console.log('End session payload:', payload);

      const response = await fetch(`${BASE_URL}/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        console.log('Session ended successfully:', this.sessionId);
      } else {
        const errorText = await response.text();
        console.error('Failed to end session:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error ending session:', error);
    } finally {
      // Clean up
      this.stopHeartbeat();
      this.isSessionActive = false;
      this.sessionId = null;
      sessionStorage.removeItem('currentSessionId');
      sessionStorage.removeItem('sessionStartTime');
    }
  }

  // Handle visibility change (tab switch)
  handleVisibilityChange = async () => {
    if (document.visibilityState === 'hidden') {
      // User switched away from tab - end session
      console.log('Tab hidden - ending session');
      await this.endSession();
    } else if (document.visibilityState === 'visible') {
      // User came back to tab - start new session
      console.log('Tab visible - starting new session');
      await this.startSession();
    }
  }

  // Initialize session management (call this on app load/login)
  async initialize() {
    // Start session
    await this.startSession();
    
    // Add visibility change listener for tab switching
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Add beforeunload listener for page close/refresh
    window.addEventListener('beforeunload', () => {
      this.endSessionSync();
    });
    
    console.log('Session service initialized');
  }

  // Synchronous end session for beforeunload (uses sendBeacon)
  endSessionSync() {
    if (!this.sessionId) {
      this.sessionId = sessionStorage.getItem('currentSessionId');
    }
    
    if (this.sessionId) {
      const token = this.getAccessToken();
      
      const payload = {
        sessionId: this.sessionId,
        endedAt: this.getCurrentTimestamp(),
        token: token // Include token in payload since sendBeacon doesn't support headers
      };
      
      // Use sendBeacon with Blob to set content-type
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      
      // Use sendBeacon for reliable delivery during page unload
      navigator.sendBeacon(`${BASE_URL}/end`, blob);
      console.log('Session end beacon sent');
    }
  }

  // Cleanup (call this on logout)
  async cleanup() {
    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // End session
    await this.endSession();
    
    console.log('Session service cleaned up');
  }
}

// Create singleton instance
const sessionService = new SessionService();

export default sessionService;
